import { createAsyncThunk } from '@reduxjs/toolkit';
import { ClinicsAPI } from 'integration/api/clinics';
import {
  ClinicOwnerActions,
  ClinicsActions,
} from 'applicaiton/constants/actions';

import { AxiosError } from 'axios';
import { ClinicCreationDto, ClinicUpdateDto } from '@docbay/schemas';
import { searchClinicsInChainProps } from 'common/types/clinicOwner';
import { ClinicOwnerAPI } from 'integration/api/clinicOwner';

/**
 * Fetches all clinics.
 *
 * @returns {Promise<AxiosResponse<any>>} The API response containing all clinics data.
 */
export const fetchClinics = createAsyncThunk(
  ClinicsActions.fetchProfiles,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await ClinicsAPI.fetchAllClinics();
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches all insurances.
 *
 * @returns {Promise<AxiosResponse<any>>} The API response containing all insurances data.
 */
export const fetchInsurances = createAsyncThunk(
  ClinicsActions.fetchInsurances,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await ClinicsAPI.fetchInsurances();
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes a clinic profile.
 *
 * @param {string} id - The ID of the clinic profile to delete.
 * @returns {Promise<AxiosResponse<any>>} The API response containing the deleted clinic data.
 */
export const deleteProfile = createAsyncThunk(
  ClinicsActions.deleteProfile,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ClinicsAPI.removeProfile(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches a specific clinic profile.
 *
 * @param {string} id - The ID of the clinic profile to fetch.
 * @returns {Promise<AxiosResponse<any>>} The API response containing the fetched clinic data.
 */
export const fetchClinicProfile = createAsyncThunk(
  ClinicsActions.getClinicProfile,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ClinicsAPI.getClinicProfile(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Saves a clinic profile.
 *
 * @param {object} props - An object containing the updated clinic data and its ID.
 * @returns {Promise<AxiosResponse<any>>} The API response containing the saved clinic data.
 */
export const saveClinicProfile = createAsyncThunk(
  ClinicsActions.saveClinicProfile,
  async (props: { data: ClinicUpdateDto; id: string }, { rejectWithValue }) => {
    try {
      const { data } = await ClinicsAPI.saveClinicProfileData(
        props.id,
        props.data,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Creates a clinic profile.
 *
 * @param {ClinicCreationDto} data - The data for the new clinic profile to create.
 * @returns {Promise<AxiosResponse<any>>} The API response containing the created clinic data.
 */
export const createClinicProfile = createAsyncThunk(
  ClinicsActions.createClinicProfile,
  async (data: ClinicCreationDto, { rejectWithValue }) => {
    try {
      const { data: responseData } = await ClinicsAPI.createClinicProfileData(
        data,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Searches for clinics in a chain.
 *
 * @param {searchClinicsInChainProps} params - The parameters to search for clinics in the chain.
 * @returns {Promise<AxiosResponse<any>>} The API response containing the searched clinics data.
 */
export const searchClinicsInChain = createAsyncThunk(
  ClinicOwnerActions.searchClinicsInChain,
  async (params: searchClinicsInChainProps, { rejectWithValue }) => {
    try {
      const { data } = await ClinicOwnerAPI.searchClinicsInChain(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the specializations of a specific clinic.
 *
 * @param {string} clinicId - The ID of the clinic to fetch its specializations.
 * @returns {Promise<AxiosResponse<any>>} The API response containing the fetched specializations data.
 */
export const getSpecializationsByClinic = createAsyncThunk(
  ClinicsActions.getSpecializationsByClinic,
  async (clinicId: string, { rejectWithValue }) => {
    try {
      const { data } = await ClinicsAPI.getSpecializationsByClinic(clinicId);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
